// v2.10.0 — HealthCard : carte d'un professionnel/établissement médical.
// Variantes : medecin (avec spécialité + horaires + tarif consultation) /
// etablissement (avec services + horaires_complets + garde_actuelle / urgence).
//
// Boutons :
//   🧭 Y aller   → /navigate?dest={lat},{lng}&code={adresse_civ}&label=...
//   📞 Appeler   → tel:{telephone}
//   🚨 Urgence   → tel:{telephone_urgence}  (visible UNIQUEMENT si dispo)
//   📋 Copier   → presse-papier structuré

import { toast } from "sonner";
import { useState } from "react";
import SponsoredBadge, { VerifiedTick } from "./SponsoredBadge";
import AppointmentModal from "./AppointmentModal";

function _hashColor(str) {
  const palette = [
    "#0EA5E9", "#06B6D4", "#10B981", "#16A34A",
    "#A855F7", "#7C3AED", "#EC4899", "#DB2777",
    "#F59E0B", "#EA580C", "#0891B2", "#1E40AF",
  ];
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function _initials(name) {
  if (!name) return "?";
  const cleaned = name.replace(/^Dr\s+/i, "").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export default function HealthCard({ profile, onNavigate }) {
  const [apptOpen, setApptOpen] = useState(false);
  if (!profile) return null;
  const isUrgence = profile.categorie_etablissement === "urgence";
  const isPharmaGarde = profile.categorie_etablissement === "pharmacie" && profile.garde_actuelle === true;
  const isMedecin = profile.type === "medecin";
  const tier = profile.sponsored_tier || "free";
  const verified = profile.verified === true;
  // Récup userId localStorage (mêmes clés que pages publiques).
  const userId = (() => { try { return localStorage.getItem("civ_ai_user_id") || "u_anon"; } catch (_) { return "u_anon"; } })();
  const avatarColor = _hashColor(profile.nom || profile.id);
  const initials = _initials(profile.nom);

  const goNavigate = () => {
    const label = profile.specialite || profile.categorie_etablissement || profile.nom;
    const url = `/navigate?dest=${profile.lat},${profile.lng}&code=${encodeURIComponent(profile.adresse_civ)}&label=${encodeURIComponent(label)}`;
    if (onNavigate) onNavigate(url, profile);
    else window.location.href = url;
  };

  const callPhone = (which) => {
    const tel = which === "urgence" ? profile.telephone_urgence : profile.telephone;
    if (!tel) { toast.error("Numéro indisponible"); return; }
    window.location.href = `tel:${tel.replace(/\s+/g, "")}`;
  };

  const copyProfile = async () => {
    const lines = [
      profile.nom,
      profile.specialite ? `Spécialité : ${profile.specialite}` : null,
      profile.etablissement ? `Établissement : ${profile.etablissement}` : null,
      profile.categorie_etablissement ? `Type : ${profile.categorie_etablissement}` : null,
      profile.adresse_civ ? `📍 ${profile.adresse_civ} — ${profile.adresse_textuelle || ""}` : null,
      profile.telephone ? `📞 ${profile.telephone}` : null,
      profile.telephone_urgence ? `🚨 URGENCE : ${profile.telephone_urgence}` : null,
      profile.horaires ? `🕐 ${profile.horaires}` : null,
      profile.horaires_complets ? `🕐 ${profile.horaires_complets}` : null,
      profile.consultation_estimee ? `💰 ${profile.consultation_estimee}` : null,
      Array.isArray(profile.services_proposes) ? `Services : ${profile.services_proposes.join(", ")}` : null,
    ].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(lines);
      toast.success("Profil copié");
    } catch (_) { toast.error("Impossible de copier"); }
  };

  // ---- Variante URGENCE (rouge intense, bouton géant) ----
  if (isUrgence) {
    return (
      <div
        data-testid={`health-card-${profile.id}`}
        className="civ-mode-card civ-mode-card-urgence"
        style={{
          background: "linear-gradient(180deg, rgba(220,38,38,0.18) 0%, rgba(220,38,38,0.06) 100%)",
          border: "2px solid #DC2626",
          borderRadius: 14, padding: 16,
          color: "#fff",
          minWidth: 280, maxWidth: 320, width: 280, flex: "0 0 auto", scrollSnapAlign: "start",
          boxShadow: "0 4px 16px rgba(220,38,38,0.25)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: "#DC2626", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, flexShrink: 0,
          }}>🚨</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, color: "#fca5a5", fontWeight: 700, letterSpacing: "0.06em" }}>URGENCE 24/24</div>
            <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.25 }}>{profile.nom}</div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => callPhone("urgence")}
          data-testid="health-btn-urgence-big"
          style={{
            width: "100%",
            background: "#DC2626", color: "#fff", border: "none",
            padding: "16px 12px", borderRadius: 12,
            fontWeight: 800, fontSize: 16,
            cursor: "pointer", marginBottom: 10,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            boxShadow: "0 4px 12px rgba(220,38,38,0.4)",
          }}
        >
          📞 APPELER {profile.telephone_urgence || profile.telephone}
        </button>

        <div style={{ fontSize: 11.5, color: "#fca5a5", lineHeight: 1.5, marginBottom: 8 }}>
          📍 <code style={{ color: "#fff" }}>{profile.adresse_civ}</code>
          <div style={{ color: "#fca5a5", marginLeft: 18, fontSize: 11 }}>{profile.adresse_textuelle}</div>
        </div>

        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={goNavigate} data-testid="health-btn-navigate" style={{
            flex: 1, background: "rgba(255,255,255,0.12)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.2)",
            padding: "8px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
          }}>🧭 Y aller</button>
          <button onClick={copyProfile} data-testid="health-btn-copy" style={{
            flex: 1, background: "rgba(255,255,255,0.08)", color: "#fff",
            border: "1px solid rgba(255,255,255,0.15)",
            padding: "8px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
          }}>📋 Copier</button>
        </div>
      </div>
    );
  }

  // ---- Variante STANDARD (medecin + autres établissements) ----
  return (
    <div data-testid={`health-card-${profile.id}`} className="civ-mode-card" style={{
      background: "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
      border: `1px solid ${isPharmaGarde ? "#10B981" : "rgba(255,255,255,0.12)"}`,
      borderRadius: 14, padding: 16,
      color: "#fff", fontFamily: "system-ui, -apple-system, sans-serif",
      minWidth: 280, maxWidth: 320, width: 280, flex: "0 0 auto", scrollSnapAlign: "start",
      position: "relative",
    }}>
      {isPharmaGarde && (
        <span style={{
          position: "absolute", top: 8, right: 8,
          background: "#10B981", color: "#fff",
          padding: "3px 9px", borderRadius: 999,
          fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em",
          animation: "civ-imm-pulse 1.8s ease-in-out infinite",
        }} data-testid="pharma-garde-badge">🚨 GARDE</span>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div style={{
          width: 56, height: 56, borderRadius: isMedecin ? "50%" : 12,
          background: avatarColor, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: isMedecin ? 18 : 24, fontWeight: 700, flexShrink: 0,
        }} data-testid="health-avatar">
          {isMedecin ? initials : (profile.categorie_etablissement === "hopital" ? "🏛" : profile.categorie_etablissement === "clinique" ? "🏥" : profile.categorie_etablissement === "pharmacie" ? "💊" : profile.categorie_etablissement === "laboratoire" ? "🧪" : "🏥")}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, color: avatarColor, fontWeight: 700, letterSpacing: "0.04em" }}>
            {isMedecin ? "👨‍⚕️ MÉDECIN" : `🏥 ${(profile.categorie_etablissement || "ÉTABLISSEMENT").toUpperCase()}`}
          </div>
          {(tier !== "free" || verified) && (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4, flexWrap: "wrap" }} data-testid={`badges-${profile.id}`}>
              {tier !== "free" && <SponsoredBadge tier={tier} size="sm" />}
              {verified && tier === "free" && <VerifiedTick size="sm" />}
            </div>
          )}
          <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25, marginTop: 2, display: "inline-flex", alignItems: "center" }}>
            {profile.nom}
            {verified && tier !== "free" && <VerifiedTick size="sm" />}
          </div>
          {profile.specialite && <div style={{ fontSize: 12, color: "#cbd5e1", marginTop: 2 }}>⚕️ {profile.specialite}</div>}
        </div>
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10, marginBottom: 10, fontSize: 12, lineHeight: 1.55 }}>
        {profile.etablissement && (
          <div style={{ color: "#cbd5e1" }}>🏥 <span style={{ color: "#fff" }}>{profile.etablissement}</span></div>
        )}
        <div style={{ color: "#cbd5e1" }}>
          📍 <code style={{ fontSize: 11, color: "#fff" }}>{profile.adresse_civ}</code>
        </div>
        <div style={{ color: "#94a3b8", fontSize: 11, marginLeft: 18 }}>{profile.adresse_textuelle}</div>
        <div style={{ color: "#cbd5e1", marginTop: 4 }}>📞 <span style={{ color: "#fff" }}>{profile.telephone}</span></div>
        {profile.telephone_urgence && (
          <div style={{ color: "#fca5a5", marginTop: 4 }}>🚨 <span style={{ color: "#fff", fontWeight: 700 }}>{profile.telephone_urgence}</span></div>
        )}
        {profile.annees_experience != null && (
          <div style={{ color: "#cbd5e1", marginTop: 4 }}>📅 {profile.annees_experience} ans d'expérience</div>
        )}
        {(profile.horaires || profile.horaires_complets) && (
          <div style={{ color: "#cbd5e1", marginTop: 4 }}>🕐 {profile.horaires || profile.horaires_complets}</div>
        )}
        {Array.isArray(profile.langues_parlees) && profile.langues_parlees.length > 0 && (
          <div style={{ color: "#cbd5e1", marginTop: 4 }}>💬 {profile.langues_parlees.join(", ")}</div>
        )}
        {profile.consultation_estimee && (
          <div style={{ color: "#cbd5e1", marginTop: 4 }}>💰 {profile.consultation_estimee}</div>
        )}
        {Array.isArray(profile.services_proposes) && profile.services_proposes.length > 0 && (
          <div style={{ color: "#94a3b8", marginTop: 4, fontSize: 11 }}>{profile.services_proposes.slice(0, 3).join(" · ")}</div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
        <button onClick={goNavigate} data-testid="health-btn-navigate" style={{
          flex: 1, background: "#3B82F6", color: "#fff", border: "none",
          padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
        }}>🧭 Y aller</button>
        <button onClick={() => callPhone("standard")} data-testid="health-btn-call" style={{
          flex: 1, background: "#16A34A", color: "#fff", border: "none",
          padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
        }}>📞 Appeler</button>
        {profile.telephone_urgence && (
          <button onClick={() => callPhone("urgence")} data-testid="health-btn-urgence" style={{
            flex: 1, background: "#DC2626", color: "#fff", border: "none",
            padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12,
          }}>🚨 Urgence</button>
        )}
        {isMedecin && (
          <button onClick={() => setApptOpen(true)} data-testid="health-btn-rdv" style={{
            flex: 1, background: "#7C3AED", color: "#fff", border: "none",
            padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 12,
          }}>📅 RDV</button>
        )}
        <button onClick={copyProfile} data-testid="health-btn-copy" style={{
          flex: 1, background: "rgba(255,255,255,0.08)", color: "#fff",
          border: "1px solid rgba(255,255,255,0.15)",
          padding: "8px 6px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12,
        }}>📋 Copier</button>
      </div>
      {isMedecin && (
        <AppointmentModal
          open={apptOpen}
          onClose={() => setApptOpen(false)}
          profile={profile}
          userId={userId}
        />
      )}
    </div>
  );
}
